import * as types from '../actions/types';

/**
 * Function to get the earliest delivery date from the list of delivery dates
 * @param {Object[]} deliveryDates - list of delivery dates
 * @returns {Date} - earliest delivery date
 */
function getEarliestDeliveryDate(deliveryDates) {
    const dates = deliveryDates.map(date => new Date(date.date));
    if (dates.length === 0) {
        return null;
    }

    return new Date(Math.min.apply(null, dates));
}

/**
 * Function to update the dependant fields based on the selection status
 * @param {Object} addOn - add on object 
 * @param {String[]} relatedTo - list of related addon ids
 * @param {Object} fieldTypeWiseSelection - object containing the selection status of add ons based on field type
 * @param {Object} fieldWiseSelection - object containing the selection status of add ons
 * @returns {void}
 */
function updateDependantFields(addOn, relatedTo = [], fieldTypeWiseSelection, fieldWiseSelection = {}) {
    if (relatedTo.length === 0) return;

    const disabledForSelection = relatedTo.some(relatedId => !fieldTypeWiseSelection[relatedId]);
    if(disabledForSelection){
        addOn.disabled = disabledForSelection;
        addOn.selected = false;
        fieldWiseSelection[addOn.externalAddOnId] = false;
    }
}

/**
 * 
 * @param {Object[]} addOns - list of add ons
 * @returns {Object} - updated add ons in the form of object
 * @example
 * Example input:
 * [
 *   {
 *     externalAddOnType: 'installation',
 *     externalAddOnId: '5246604',
 *     externalAddOnName: 'Refrigerator Install to Existing or No Water (Ready To Use)',
 *     externalAddOnDescription: 'Have an Installer quickly and professionally connect your Refrigerator to an existing water supply line. We will install a manufacturer supplied water filter or new water filter, if purchased. We will also level the new appliance and demonstrate the basic operation.',
 *     externalAddOnPrice: 24.99,
 *     selected: false,
 *     interDependencyRules: {
 *       id: '5246604',
 *       value: 'installation',
 *       relation: 'parent',
 *       relatedTo: [],
 *       requiresParts: false,
 *       fieldType: 'radio'
 *     },
 *      "externalAddOnModalInfo": {
 *         "icon": "screw-driver",
 *         "modalBody": "message to show in modal body",
 *         "modalContentLabel": "label to trigger the modal",
 *         "modalSubTitle": "sub-title",
 *         "modalTitle": "title",
 *         "showBreakUp": false
 *      }
 *   }
 * ]
 * 
 * Example output:
 * {
 *   '5246604': {
 *     externalAddOnType: 'installation',
 *     externalAddOnId: '5246604',
 *     externalAddOnName: 'Refrigerator Install to Existing or No Water (Ready To Use)',
 *     externalAddOnDescription: 'Have an Installer quickly and professionally connect your Refrigerator to an existing water supply line. We will install a manufacturer supplied water filter or new water filter, if purchased. We will also level the new appliance and demonstrate the basic operation.',
 *     externalAddOnPrice: 24.99,
 *     selected: false,
 *     interDependencyRules: {
 *       id: '5246604',
 *       value: 'installation',
 *       relation: 'parent',
 *       relatedTo: [],
 *       requiresParts: false,
 *       fieldType: 'radio'
 *     },
 *        "externalAddOnModalInfo": {
 *         "icon": "screw-driver",
 *         "modalBody": "message to show in modal body",
 *         "modalContentLabel": "label to trigger the modal",
 *         "modalSubTitle": "sub-title",
 *         "modalTitle": "title",
 *         "showBreakUp": false
 *      }
 *   }
 *   },
 *   selection: {
 *     '5246604': false,
 *   },
 *   ids: [
 *     '5246604',
 *   ],
 *   groupedByFieldTypes: {
 *     radio: {
 *       installation: [
 *         '5246604',
 *       ]
 *     },
 *   }
 * }
 */
function getAddOnsById(addOns) {
    const updatedAddOns = addOns.reduce((acc, addOn) => {
        const {
            externalAddOnId,
            externalAddOnType,
        } = addOn;

        acc[externalAddOnId] = addOn;
        acc.selection[externalAddOnId] = addOn.selected;
        acc.selectionByFieldType[externalAddOnType] = acc.selectionByFieldType[externalAddOnType] || addOn.selected;
        acc.ids.push(externalAddOnId);

        if(externalAddOnType in acc.groupedByFieldTypes['checkbox']){
            if(acc.groupedByFieldTypes['checkbox'][externalAddOnType]){
                acc.groupedByFieldTypes['radio'][externalAddOnType] = acc.groupedByFieldTypes['checkbox'][externalAddOnType];
                delete acc.groupedByFieldTypes['checkbox'][externalAddOnType];
            }
            acc.groupedByFieldTypes['radio'][externalAddOnType].push(externalAddOnId);
        } else {
            acc.groupedByFieldTypes['checkbox'][externalAddOnType] = [externalAddOnId];
        }
        return acc;
    }, { selection: {}, ids: [], groupedByFieldTypes: {'checkbox': {}, 'radio': {}}, selectionByFieldType: {} });

    updatedAddOns.ids.forEach(id => {
        const addOn = updatedAddOns[id];
        const {
            interDependencyRules: {
                relatedTo = [],
                relation
            } = {}
        } = addOn;
        if (relation === 'dependsOn') {
            updateDependantFields(addOn, relatedTo, updatedAddOns.selectionByFieldType, updatedAddOns.selection);
        }
    });

    return updatedAddOns;
}

/**
 * Function to reset the addons selection and disable them if error is present
 * @param {Object[]} addOns - list of add ons
 * @param {Boolean} error - flag to indicate error
 * @returns {Object[]} - mutated add ons
 */
function resetAddOnsSelection(addOns, error) {
    const { ids = [] } = addOns;
    ids.forEach(addOnId => {
        addOns[addOnId] = {
            ...addOns[addOnId],
            selected: false,
            disabled: error,
        }
        addOns.selection[addOnId] = false;
        addOns.selectionByFieldType = {};
        updateDependantFields(addOns[addOnId], addOns[addOnId].interDependencyRules.relatedTo, addOns.selectionByFieldType, addOns.selection);
    });

    return addOns;
}

function setAddOnsSelection(addOns, addOnID, selected) {
    const { ids = [], groupedByFieldTypes = {} } = addOns;
    const updatedAddOnsId = addOnID;
    const addOn = addOns[updatedAddOnsId];
    const {
        externalAddOnType
    } = addOn;

    //reset radio buttons
    const radioGroups = groupedByFieldTypes['radio'];
    if (
        radioGroups && 
        radioGroups[externalAddOnType] && 
        radioGroups[externalAddOnType].indexOf(updatedAddOnsId) !== -1
    ) {
        radioGroups[externalAddOnType].forEach(id => {
            let addOn = addOns[id];
            addOn = {
                ...addOns[id],
                selected: false,
            }
            addOns.selection[id] = false;
            addOns[id] = addOn;
        })
    }

    ids.forEach(addOnId => {
        let addOn = addOns[addOnId];
        addOn = {
            ...addOns[addOnId],
            selected: addOnId === updatedAddOnsId ? selected : addOns[addOnId].selected,
        }

        addOns.selection[addOnId] = addOnId === updatedAddOnsId ? selected : addOns.selection[addOnId];
        addOns.selectionByFieldType[addOn.externalAddOnType] = addOnId === updatedAddOnsId ? selected : addOns.selectionByFieldType[addOn.externalAddOnType];
        if(addOnId !== updatedAddOnsId){
            updateDependantFields(addOn, addOn.interDependencyRules.relatedTo, addOns.selectionByFieldType, addOns.selection);
        }

        addOns[addOnId] = addOn;
    });

    return addOns;
}

/**
 * Wrapper reducer to handle external add ons for multiple products
 * @param {Object} state - state of the external add ons 
 * @param {Object} action - intended action to be performed 
 * @returns {Object} - mutated state
 */
export default function externalAddOnsReducerForProducts(state = {}, action) {
    const { productId } = action;

    switch (action.type) {
        case types.RECEIVE_EXTERNAL_ADDONS:
        case types.UPDATE_ZIPCDOE:
        case types.SET_ERROR:
        case types.SET_ADD_ON_SELECTION:
        case types.SET_ADD_ON_IN_FLIGHT: {
            return {
                ...state,
                [productId]: externalAddOnsReducer(state[productId], action)
            }
        }
        case types.RECEIVE_EXTERNAL_ADDONS_BULK:{
            const { externalAddOns } = action;
            const productIds = Object.keys(externalAddOns);

            return productIds.reduce((acc,id) => {
                acc[id] = externalAddOnsReducer(state[id], {
                        type: types.RECEIVE_EXTERNAL_ADDONS,
                        externalAddOns: externalAddOns[id],
                    });
                return acc;
            }, {});
        }
        case types.RESET_EXTERNAL_ADDONS: {
            const { productId } = action;
            const productIds = Object.keys(state);

            return productIds.reduce((acc, id) => {
                if(id === productId){
                    acc[id] = { ...state[id] }
                }

                return acc;
            }, {});
        }
        default: {
            return state;
        }
    }
}


/**
 * External Add ons base reducer to update the state for external add ons for single product
 * @param {Object} state - state of the external add ons 
 * @param {Object} action - intended action to be performed 
 * @returns {Object} - mutated state
 */
export function externalAddOnsReducer(
    state = { 
        zipCode: null,
        error: false,
        errorMessage: '',
        externalAddOns: {
          addOns: {
            ids: [],
            selection: {},
            groupedByFieldTypes: {}
          }
        },
        productDeliveryDates: {},
        earliestDeliveryDate: null,
        inFlight: false
    },
    action
) {
    switch (action.type) {
        case types.RECEIVE_EXTERNAL_ADDONS:
            const {
                productDeliveryDates = {},
                externalAddOns = {},
            } = action.externalAddOns;

            const earliestDeliveryDate = getEarliestDeliveryDate(productDeliveryDates.deliveryStates);
            const error = productDeliveryDates.error || externalAddOns.error;
            const errorMessage = productDeliveryDates.errorMessage || externalAddOns.errorMessage;
            const addOnsById = getAddOnsById(externalAddOns.addOns);

            return {
                ...state,
                productDeliveryDates,
                externalAddOns: {
                    ...externalAddOns,
                    addOns: addOnsById
                },
                earliestDeliveryDate,
                error,
                errorMessage,
                inFlight: false,
                zipCode: externalAddOns.zipCode,
            }
        case types.UPDATE_ZIPCDOE: {
            const { zipCode, error, errorMessage } = action;
            return {
                ...state,
                zipCode,
                error,
                errorMessage,
                earliestDeliveryDate: error ? null : state.earliestDeliveryDate,
                externalAddOns: {
                    ...state.externalAddOns,
                    addOns: resetAddOnsSelection(state.externalAddOns.addOns, error),
                    informationMessage: '',
                }
            }
        }
        case types.SET_ERROR: {
            const { error, errorMessage } = action;
            return {
                ...state,
                error,
                errorMessage,
            }
        }
        case types.SET_ADD_ON_SELECTION: {
            const { addOnID, selected } = action;
            const { externalAddOns } = state;

            return {
                ...state,
                externalAddOns: {
                    ...externalAddOns,
                    addOns: setAddOnsSelection(externalAddOns.addOns, addOnID, selected),
                }
            }
        }
        case types.SET_ADD_ON_IN_FLIGHT: {
            const { inFlight } = action;
            return {
                ...state,
                inFlight
            }
        }
        default:
            return state;
    }
}
